// pipelineDiscoveryService.js
import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js';
import { readApiError } from '../utils/parseApiErrorMessage.js';

export const pipelineDiscoveryService = {
  /**
   * Discover pipelines (Glue, Step Functions, EMR) for an AWS account
   */
  discoverPipelines: async (clientId, awsAccountId, options = {}) => {
    const {
      regions = null,
      autoOnboard = false,
      onboardCloudwatch = true,
      onboardS3 = true
    } = options;

    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/pipeline-discovery/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        aws_account_id: awsAccountId,
        regions: regions,
        auto_onboard: autoOnboard,
        onboard_cloudwatch: onboardCloudwatch,
        onboard_s3: onboardS3
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to discover pipelines: ${errorText}`);
    }

    return await response.json();
  },

  /**
   * Auto-onboard discovered log paths and metrics
   */
  autoOnboardLogs: async (clientId, awsAccountId, logPaths, options = {}) => {
    const {
      onboardCloudwatch = true,
      onboardS3 = true,
      onboardMetrics = false,
      metrics = []
    } = options;

    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/pipeline-discovery/auto-onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        aws_account_id: awsAccountId,
        log_paths: {
          cloudwatch: logPaths.cloudwatch || [],
          s3: logPaths.s3 || []
        },
        metrics: metrics || [],
        onboard_cloudwatch: onboardCloudwatch,
        onboard_s3: onboardS3,
        onboard_metrics: onboardMetrics
      })
    });

    if (!response.ok) {
      throw await readApiError(response, "Failed to onboard logs");
    }

    return await response.json();
  },

  /**
   * GET endpoint for pipeline discovery (convenience)
   */
  discoverPipelinesGet: async (clientId, awsAccountId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.regions) {
      queryParams.append('regions', Array.isArray(params.regions) ? params.regions.join(',') : params.regions);
    }
    if (params.autoOnboard !== undefined) {
      queryParams.append('auto_onboard', params.autoOnboard);
    }
    if (params.onboardCloudwatch !== undefined) {
      queryParams.append('onboard_cloudwatch', params.onboardCloudwatch);
    }
    if (params.onboardS3 !== undefined) {
      queryParams.append('onboard_s3', params.onboardS3);
    }

    const url = `${API_BASE_URL}/api/v1/pipeline-discovery/discover/${clientId}/${awsAccountId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetchWrapper(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to discover pipelines: ${errorText}`);
    }

    return await response.json();
  },

  /**
   * Get pipeline resources (details) by pipeline ID
   * GET /api/v1/pipeline-discovery/{pipeline_id}
   * @param {string} pipelineId - Pipeline identifier
   * @param {Object} options - Optional { clientId, awsAccountId } for multi-tenant filtering
   */
  getPipelineDetails: async (pipelineId, options = {}) => {
    try {
      if (!pipelineId) {
        throw new Error('pipelineId is required');
      }
      const params = new URLSearchParams();
      if (options.clientId) params.append('client_id', options.clientId);
      if (options.awsAccountId) params.append('aws_account_id', options.awsAccountId);
      const qs = params.toString();
      const url = `${API_BASE_URL}/api/v1/pipeline-discovery/${encodeURIComponent(pipelineId)}${qs ? '?' + qs : ''}`;
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get pipeline details: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[getPipelineDetails]', error);
      throw error;
    }
  },

  /**
   * Get logs by pipeline ID
   * GET /api/v1/pipeline-discovery/logs/{pipeline_id}
   * @param {string} pipelineId - Pipeline identifier
   * @param {Object} options - Optional { clientId, awsAccountId } for multi-tenant filtering
   */
  getLogsByPipeline: async (pipelineId, options = {}) => {
    if (!pipelineId) {
      throw new Error('pipelineId is required');
    }
    const params = new URLSearchParams();
    if (options.clientId) params.append('client_id', options.clientId);
    if (options.awsAccountId) params.append('aws_account_id', options.awsAccountId);
    const qs = params.toString();
    const url = `${API_BASE_URL}/api/v1/pipeline-discovery/logs/${encodeURIComponent(pipelineId)}${qs ? '?' + qs : ''}`;
    const response = await fetchWrapper(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get logs by pipeline: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Get metrics by pipeline ID
   * GET /api/v1/pipeline-discovery/metrics/{pipeline_id}
   * @param {string} pipelineId - Pipeline identifier
   * @param {Object} options - Optional { clientId, awsAccountId } for multi-tenant filtering
   */
  getMetricsByPipeline: async (pipelineId, options = {}) => {
    if (!pipelineId) {
      throw new Error('pipelineId is required');
    }
    const params = new URLSearchParams();
    if (options.clientId) params.append('client_id', options.clientId);
    if (options.awsAccountId) params.append('aws_account_id', options.awsAccountId);
    const qs = params.toString();
    const url = `${API_BASE_URL}/api/v1/pipeline-discovery/metrics/${encodeURIComponent(pipelineId)}${qs ? '?' + qs : ''}`;
    const response = await fetchWrapper(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get metrics by pipeline: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Get lineage config by pipeline ID
   * GET /api/v1/pipeline-discovery/lineage/{pipeline_id}?client_id=&aws_account_id=
   */
  getPipelineLineage: async (pipelineId, clientId, awsAccountId) => {
    if (!pipelineId || !clientId || !awsAccountId) {
      throw new Error('pipelineId, clientId and awsAccountId are required');
    }
    const url = `${API_BASE_URL}/api/v1/pipeline-discovery/lineage/${encodeURIComponent(pipelineId)}?client_id=${encodeURIComponent(clientId)}&aws_account_id=${encodeURIComponent(awsAccountId)}`;
    const response = await fetchWrapper(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get lineage config: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Resolve upstream/downstream lineage graph from saved Workflow Builder diagrams.
   * GET /api/v1/pipeline-discovery/lineage/{pipeline_id}/graph
   */
  getPipelineLineageGraph: async (pipelineId, clientId, awsAccountId, hints = {}) => {
    if (!pipelineId || !clientId || !awsAccountId) {
      throw new Error('pipelineId, clientId and awsAccountId are required');
    }
    const params = new URLSearchParams({
      client_id: clientId,
      aws_account_id: awsAccountId,
    });
    if (hints.pipelineName) params.append('pipeline_name', hints.pipelineName);
    if (hints.pipelineType) params.append('pipeline_type', hints.pipelineType);
    if (hints.pipelineRegion) params.append('pipeline_region', hints.pipelineRegion);
    if (hints.includeResources === false) params.append('include_resources', 'false');

    const url = `${API_BASE_URL}/api/v1/pipeline-discovery/lineage/${encodeURIComponent(pipelineId)}/graph?${params.toString()}`;
    const response = await fetchWrapper(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get lineage graph: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Enable/disable lineage for a pipeline
   * POST /api/v1/pipeline-discovery/lineage
   */
  upsertPipelineLineage: async ({
    clientId,
    awsAccountId,
    pipelineId,
    enabled,
    pipelineName,
    pipelineType,
    pipelineRegion,
  }) => {
    if (!clientId || !awsAccountId || !pipelineId) {
      throw new Error('clientId, awsAccountId and pipelineId are required');
    }
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/pipeline-discovery/lineage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        aws_account_id: awsAccountId,
        pipeline_id: pipelineId,
        enabled: Boolean(enabled),
        pipeline_name: pipelineName || null,
        pipeline_type: pipelineType || null,
        pipeline_region: pipelineRegion || null,
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update lineage config: ${errorText}`);
    }
    return await response.json();
  },

  /**
   * Get pipeline onboarding status summary (fast, DynamoDB only).
   * GET /api/v1/pipeline-discovery/status?client_id=&aws_account_id=
   * Returns: { total, full, partial, none }
   */
  getPipelineStatus: async (clientId, awsAccountId) => {
    try {
      if (!clientId || !awsAccountId) {
        throw new Error('clientId and awsAccountId are required');
      }
      const url = `${API_BASE_URL}/api/v1/pipeline-discovery/status?client_id=${encodeURIComponent(clientId)}&aws_account_id=${encodeURIComponent(awsAccountId)}`;
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get pipeline status: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[getPipelineStatus]', error);
      throw error;
    }
  },

  /**
   * Get onboarded pipelines (from DynamoDB).
   * GET /api/v1/pipeline-discovery/onboarded/{client_id}/{aws_account_id}
   * Returns: { pipelines: [{ pipeline_id, pipeline_name, pipeline_type, region, logs_onboarded, metrics_onboarded, status }], count }
   */
  getOnboardedPipelines: async (clientId, awsAccountId) => {
    try {
      if (!clientId || !awsAccountId) {
        throw new Error('clientId and awsAccountId are required');
      }
      const url = `${API_BASE_URL}/api/v1/pipeline-discovery/onboarded/${encodeURIComponent(clientId)}/${encodeURIComponent(awsAccountId)}`;
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get onboarded pipelines: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('[getOnboardedPipelines]', error);
      throw error;
    }
  },

  /**
   * Get pipeline status summary - calls backend API and maps response to legacy shape.
   * Returns: { total, fullyOnboarded, partiallyOnboarded, notOnboarded }
   */
  getPipelineStatusSummary: async (clientId, awsAccountId) => {
    try {
      if (!clientId || !awsAccountId) {
        throw new Error('clientId and awsAccountId are required');
      }
      const data = await pipelineDiscoveryService.getPipelineStatus(clientId, awsAccountId);
      return {
        total: data.total ?? 0,
        fullyOnboarded: data.full ?? 0,
        partiallyOnboarded: data.partial ?? 0,
        notOnboarded: data.none ?? 0,
      };
    } catch (error) {
      console.error('[getPipelineStatusSummary]', error);
      throw error;
    }
  },

  /**
   * Sync pipeline onboarding - onboard remaining (non-onboarded) log paths for a pipeline.
   * Uses auto-onboard API under the hood.
   * @param {string} clientId - Client ID
   * @param {string} awsAccountId - AWS account ID
   * @param {Object} pipeline - Pipeline object from discovery (must have log_paths.cloudwatch, log_paths.s3 with onboarded flags)
   * @returns {Promise<Object>} Auto-onboard API response
   */
  syncPipelineOnboarding: async (clientId, awsAccountId, pipeline) => {
    try {
      if (!clientId || !awsAccountId) {
        throw new Error('clientId and awsAccountId are required');
      }
      if (!pipeline || !pipeline.log_paths) {
        throw new Error('pipeline with log_paths is required');
      }
      const cw = pipeline.log_paths.cloudwatch || [];
      const s3 = pipeline.log_paths.s3 || [];
      const cloudwatchLogInfos = cw
        .filter((x) => !(typeof x === 'object' ? x.onboarded : false))
        .map((x) => ({
          path: typeof x === 'object' ? x.path : x,
          onboarded: false,
          pipeline_name: pipeline.name,
          pipeline_type: pipeline.type || pipeline.pipeline_type,
          pipeline_region: pipeline.region || pipeline.pipeline_region,
        }));
      const s3LogInfos = s3
        .filter((x) => !(typeof x === 'object' ? x.onboarded : false))
        .map((x) => ({
          path: typeof x === 'object' ? x.path : x,
          onboarded: false,
          pipeline_name: pipeline.name,
          pipeline_type: pipeline.type || pipeline.pipeline_type,
          pipeline_region: pipeline.region || pipeline.pipeline_region,
        }));
      if (cloudwatchLogInfos.length === 0 && s3LogInfos.length === 0) {
        return { summary: { total_onboarded: 0 }, cloudwatch: { onboarded: [] }, s3: { onboarded: [] } };
      }
      return await pipelineDiscoveryService.autoOnboardLogs(
        clientId,
        awsAccountId,
        { cloudwatch: cloudwatchLogInfos, s3: s3LogInfos },
        {
          onboardCloudwatch: cloudwatchLogInfos.length > 0,
          onboardS3: s3LogInfos.length > 0,
        }
      );
    } catch (error) {
      console.error('[syncPipelineOnboarding]', error);
      throw error;
    }
  },

  /**
   * List saved workflow diagrams for a client.
   * GET /api/v1/architecture/list/{client_id}
   */
  listWorkflowDiagrams: async (clientId) => {
    if (!clientId) {
      throw new Error("clientId is required");
    }
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/architecture/list/${encodeURIComponent(clientId)}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to list workflows: ${errorText}`);
    }
    const data = await response.json();
    return data?.diagrams || [];
  },

  /**
   * Load a saved workflow diagram by name.
   * GET /api/v1/architecture/load/{client_id}?name={diagram_name}
   */
  loadWorkflowDiagram: async (clientId, name) => {
    if (!clientId || !name) {
      throw new Error("clientId and workflow name are required");
    }
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/architecture/load/${encodeURIComponent(clientId)}?name=${encodeURIComponent(name)}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load workflow "${name}": ${errorText}`);
    }
    const data = await response.json();
    return data?.diagram || null;
  },

  /**
   * Build a Grafana Explore deep link for a pipeline (logs, metrics, or lineage tab).
   */
  getGrafanaLink: async ({
    pipelineName,
    pipelineType,
    tab = "logs",
    logPaths = [],
    metrics = [],
    region = null,
    pipelineId = null,
    pipelineArn = null,
    environmentName = null,
    awsAccountId = null,
  }) => {
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/pipeline-discovery/grafana-link`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pipeline_name: pipelineName,
          pipeline_type: pipelineType || null,
          tab,
          log_paths: logPaths,
          metrics,
          region,
          pipeline_id: pipelineId,
          pipeline_arn: pipelineArn,
          environment_name: environmentName,
          aws_account_id: awsAccountId,
        }),
      },
    );

    if (!response.ok) {
      const message = await readApiError(response, "Failed to build Grafana link");
      throw new Error(message);
    }

    const data = await response.json();
    return data?.url || null;
  },
};
